import styled from 'styled-components';
import React, { Children } from 'react';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { FOLDER_GRID } from '../itemIds';

export type FolderGridDefinition = ItemDefinition<
  typeof FOLDER_GRID,
  Record<string, never>
>;

const GridContainer = styled.div<{ isSingleItem: boolean; itemCount: number }>`
  display: ${props => (props.isSingleItem ? 'flex' : 'grid')};
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  padding: 24px;
  width: 100%;

  /* Center items when less than full row */
  justify-content: center;
  justify-items: center;

  /* Center single item */
  ${props =>
    props.isSingleItem &&
    `
    justify-content: center;
    align-items: center;
    min-height: 200px;
  `}

  /* For grids with fewer than 4 items, use auto-fit to center them */
  ${props =>
    !props.isSingleItem &&
    props.itemCount < 4 &&
    `
    grid-template-columns: repeat(${props.itemCount}, auto);
    justify-content: center;
  `}

  /* For 5 folders: center the last one in second row */
  ${props =>
    props.itemCount === 5 &&
    `
    > div:nth-child(5) {
      grid-column: 2 / 4;
      justify-self: center;
    }
  `}

  /* For 6 items: center the last two in second row */
  ${props =>
    props.itemCount === 6 &&
    `
    &[data-item-count="${props.itemCount}"] > div:nth-child(5) {
      grid-column: 2 !important;
      justify-self: center !important;
    }
    &[data-item-count="${props.itemCount}"] > div:nth-child(6) {
      grid-column: 3 !important;
      justify-self: center !important;
    }
  `}

  /* For 7 items: 6 folders + hidden Add button */
  ${props =>
    props.itemCount === 7 &&
    `
    /* Center folders 5 and 6, ignore hidden Add button */
    &[data-item-count="${props.itemCount}"] > div:nth-child(5) {
      grid-column: 2 !important;
      justify-self: center !important;
    }
    &[data-item-count="${props.itemCount}"] > div:nth-child(6) {
      grid-column: 3 !important;
      justify-self: center !important;
    }
    /* Hide the Add button that's not rendering content */
    &[data-item-count="${props.itemCount}"] > div:nth-child(7) {
      grid-column: auto !important;
      display: none !important;
    }
  `}

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);

    /* Reset desktop-specific positioning on mobile */
    ${props =>
      (props.itemCount === 5 ||
        props.itemCount === 6 ||
        props.itemCount === 7) &&
      `
      > div:nth-child(5),
      > div:nth-child(6),
      > div:nth-child(7) {
        grid-column: auto !important;
        justify-self: center !important;
        display: block !important; /* Override the display: none for 7th item */
      }
    `}

    /* For mobile with fewer than 2 items, center them */
    ${props =>
      !props.isSingleItem &&
      props.itemCount < 2 &&
      `
      grid-template-columns: repeat(${props.itemCount}, auto);
      justify-content: center;
    `}
  }

  /* Each grid item centered */
  > div {
    justify-self: center;
  }

  /* Override folder box widths */
  > div > div {
    margin: 0 !important;
  }
`;

export class FolderGrid extends Item<
  typeof FOLDER_GRID,
  Record<string, never>
> {
  protected render({ children }: RenderProps<Record<string, never>>) {
    const childArray = Children.toArray(children);
    const isSingleItem = childArray.length === 1;
    const itemCount = childArray.length;

    return (
      <GridContainer
        isSingleItem={isSingleItem}
        itemCount={itemCount}
        data-item-count={itemCount}
      >
        {childArray.map((c, i) => (
          <div key={i} data-item={i + 1}>
            {c}
          </div>
        ))}
      </GridContainer>
    );
  }
}

registry[FOLDER_GRID] = FolderGrid;
